import React from 'react';
import { Phone, Mail, Calendar, Linkedin, Globe, UserPlus } from 'lucide-react';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_mimoit-refresh/artifacts/knmts9jj_JV_Headshot_pic.png';

const contact = {
  firstName: 'Jorge',
  lastName: 'Venero',
  title: 'Founder | Technology & Security Advisor',
  org: 'mimoIT Technology Solutions',
  phone: '+12129608056',
  phoneDisplay: '212-960-8056',
  email: 'jorge@mimoit.com',
  website: 'https://mimoit.com',
  linkedin: 'https://www.linkedin.com/in/jorge-venero/',
  scheduleUrl:
    'https://outlook.office.com/bookwithme/user/c15140a8528a4d7891685e503b9b45fc@mimoit.com/meetingtype/ldpYCSMam0mm9SI4tfOtWg2?anonymous&ismsaljsauthenabled&ep=mlink',
  addressPrimary: {
    street: '31 Vose Ave, Unit 6',
    city: 'South Orange',
    state: 'NJ',
    zip: '07079',
    country: 'USA',
  },
  addressSecondary: {
    street: '450 Lexington Ave #3101',
    city: 'New York',
    state: 'NY',
    zip: '10163',
    country: 'USA',
  },
};

const buildVCard = () => {
  const {
    firstName, lastName, title, org, phone, email,
    website, linkedin, addressPrimary, addressSecondary,
  } = contact;

  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${lastName};${firstName};;;`,
    `FN:${firstName} ${lastName}`,
    `ORG:${org}`,
    `TITLE:${title}`,
    `TEL;TYPE=CELL,VOICE:${phone}`,
    `EMAIL;TYPE=WORK,INTERNET:${email}`,
    `URL;TYPE=WORK:${website}`,
    `URL;TYPE=LinkedIn:${linkedin}`,
    `ADR;TYPE=WORK,PREF:;;${addressPrimary.street};${addressPrimary.city};${addressPrimary.state};${addressPrimary.zip};${addressPrimary.country}`,
    `ADR;TYPE=WORK:;;${addressSecondary.street};${addressSecondary.city};${addressSecondary.state};${addressSecondary.zip};${addressSecondary.country}`,
    'END:VCARD',
  ];
  return lines.join('\r\n');
};

const JorgePage = () => {
  const handleSaveContact = () => {
    const vcard = buildVCard();
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Jorge_Venero.vcf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buttons = [
    {
      label: 'Save Contact',
      icon: UserPlus,
      onClick: handleSaveContact,
      primary: true,
      testid: 'jorge-save-contact',
    },
    {
      label: 'Schedule a Meeting',
      icon: Calendar,
      href: contact.scheduleUrl,
      external: true,
      testid: 'jorge-schedule',
    },
    {
      label: 'LinkedIn',
      icon: Linkedin,
      href: contact.linkedin,
      external: true,
      testid: 'jorge-linkedin',
    },
    {
      label: 'Company Website',
      icon: Globe,
      href: contact.website,
      external: true,
      testid: 'jorge-website',
    },
    {
      label: 'Call',
      icon: Phone,
      href: `tel:${contact.phone}`,
      testid: 'jorge-call',
    },
    {
      label: 'Email',
      icon: Mail,
      href: `mailto:${contact.email}`,
      testid: 'jorge-email',
    },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 flex items-start sm:items-center justify-center px-4 py-8"
      data-testid="jorge-page"
    >
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-slate-900 via-mimo-primary to-slate-900 pt-10 pb-8 px-6 text-center">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center" />
            <div className="relative z-10 flex justify-center">
              <div className="w-32 h-32 rounded-full bg-white shadow-lg overflow-hidden ring-4 ring-white/30">
                <img
                  src={LOGO_URL}
                  alt="Jorge Venero"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Identity */}
          <div className="px-6 pt-6 pb-2 text-center">
            <h1
              className="text-2xl font-bold font-heading text-slate-900"
              data-testid="jorge-name"
            >
              Jorge Venero
            </h1>
            <p
              className="text-sm text-slate-600 mt-1"
              data-testid="jorge-title"
            >
              Founder | Technology & Security Advisor
            </p>
            <p className="text-xs text-mimo-primary font-medium mt-1 tracking-wide uppercase">
              mimoIT Technology Solutions
            </p>
          </div>

          {/* Actions */}
          <div className="px-6 pt-6 pb-8 space-y-3">
            {buttons.map((btn) => {
              const Icon = btn.icon;
              const baseClasses =
                'w-full flex items-center justify-center gap-3 rounded-full px-6 py-3.5 font-semibold text-sm sm:text-base transition-all duration-200 active:scale-[0.98]';
              const styleClasses = btn.primary
                ? 'bg-mimo-primary hover:bg-mimo-primary/90 text-white shadow-md hover:shadow-lg'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-900 border border-slate-200';

              if (btn.onClick) {
                return (
                  <button
                    key={btn.label}
                    type="button"
                    onClick={btn.onClick}
                    className={`${baseClasses} ${styleClasses}`}
                    data-testid={btn.testid}
                  >
                    <Icon className="w-5 h-5" />
                    {btn.label}
                  </button>
                );
              }

              return (
                <a
                  key={btn.label}
                  href={btn.href}
                  target={btn.external ? '_blank' : undefined}
                  rel={btn.external ? 'noopener noreferrer' : undefined}
                  className={`${baseClasses} ${styleClasses}`}
                  data-testid={btn.testid}
                >
                  <Icon className="w-5 h-5" />
                  {btn.label}
                </a>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          &copy; {new Date().getFullYear()} mimoIT Technology Solutions
        </p>
      </div>
    </div>
  );
};

export default JorgePage;
